import {pulseUserAtom} from 'atoms/auth';
import {growthbookCacheAtom} from 'atoms/growthbook';
import {
  cancelSpeculativeRun,
  handOffSpeculativeRun,
  type SpeculativeProgress,
  type SpeculativeRun,
  startSpeculativeRun,
} from 'components/ai/speculativeAsk';
import {isSuperUser} from 'helpers/utils';
import {useAtomValue} from 'jotai';
import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * How long typing must pause before the Ask turn starts. Short, so the turn is
 * further along when the question is sent; every edit cancels it, and the
 * server keeps no history, analytics or writes for a turn never sent.
 */
export const SPECULATIVE_ASK_PAUSE_MS = 350;

/**
 * Start the Ask turn for `query` once typing pauses, so it is partway answered
 * when the user sends it. Every edit cancels it and waits for the next pause,
 * and closing search cancels it unless `handOff` passed it to the Ask drawer.
 * `progress` is the run's latest step and answer preview for the Ask row.
 */
export const useSpeculativeAsk = ({query, enabled}: {query: string; enabled: boolean}) => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const {isSourcegraphEnabled} = useAtomValue(growthbookCacheAtom);
  // Same settings the drawer would ask with, so the turn it takes over matches.
  const elevated = pulseUser ? isSuperUser(pulseUser) : false;
  const [progress, setProgress] = useState<SpeculativeProgress | null>(null);
  const runRef = useRef<SpeculativeRun | null>(null);
  const trimmed = query.trim();

  useEffect(() => {
    setProgress(null);
    if (!enabled || !trimmed) {
      return;
    }
    const timer = setTimeout(() => {
      const run = startSpeculativeRun(trimmed, {
        elevated,
        enableSourcegraph: isSourcegraphEnabled,
      });
      runRef.current = run;
      run.onProgress(setProgress);
    }, SPECULATIVE_ASK_PAUSE_MS);
    return () => {
      clearTimeout(timer);
      const run = runRef.current;
      runRef.current = null;
      if (run) {
        run.onProgress(null);
        cancelSpeculativeRun(run);
      }
    };
  }, [elevated, enabled, isSourcegraphEnabled, trimmed]);

  /** Pass the run to the drawer just before search sends `question` to it. */
  const handOff = useCallback(
    (question: string) => enabled && handOffSpeculativeRun(question),
    [enabled]
  );

  return {progress, handOff};
};
